import { useState } from 'react';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { DialogDescription } from '@radix-ui/react-dialog';
import { ArchiveRestoreIcon } from 'lucide-react';
import { useUpdateTicketedEvent } from '@Hooks/useUpdateTicketedEvent';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

interface RestoreEventDialogProps {
  event: { id: string; name: string };
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function RestoreEventDialog({
  event,
  isOpen,
  setIsOpen,
}: RestoreEventDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateTicketedEvent({
    onSuccess: () => {
      toastSuccess('Event restored successfully.');
      setIsOpen(false);
    },
    onError: (err) => {
      console.error('Error restoring event:', err);
      setError('We were unable to restore this event. Please try again.');
    },
  });

  const onConfirm = async () => {
    setError(null);
    try {
      setIsSaving(true);
      await updateMutation.mutateAsync({
        eventId: event.id,
        payload: {
          event: {
            archivedAt: null,
            archivedBy: null,
          },
        },
      });
    } catch (err: any) {
      console.error('Error restoring event:', err);
      setError('We were unable to restore this event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Restore {event.name}?</DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Restoring this event will make it visible to users again.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="link" disabled={isSaving}>
              Cancel
            </Button>
          </DialogClose>

          <Button
            type="button"
            loading={isSaving}
            disabled={isSaving}
            icon={{ src: <ArchiveRestoreIcon className="h-4 w-4" /> }}
            onClick={onConfirm}
          >
            Restore
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
